// Segmentatietool — Fase 1 demo orchestrator.
import 'bootstrap/dist/css/bootstrap.min.css';
import { Collapse } from 'bootstrap';
import 'ol/ol.css';
import './demo.css';

import { state, on, emit, clearSelection, countKeyOf, sectorPctOf } from './state.js';
import * as api from './api.js';
import PC_CENTROIDS from './pcCentroids.json';
import { sectorsFromPcSelect, nis9ToWaveZone } from './nis9.js';
import { fetchCounts, lastCountSource, gisSectors } from './countProvider.js';
import { initBpostBoxes, ensureBpostBoxes, bpostSums, bpostLoading, bpostTotalOf } from './bpostBoxes.js';
import { serializeSelection, restoreSelection } from './savedSelection.js';
import { initMap, refreshStyles, flyTo, sectorsAround, sectorsInRadius,
  showAreaCircle, clearAreaCircle, fitToSelection, fetchSectorsInBBox, fitToBBox, focusFeatures, councilNameAt, sectorNis9At, fetchSectorsByCodes, interiorPointOf, fetchCouncilGeometry, sectorsInsideGeometry, setSelectEnabled, startDrawCircle, startDrawPolygon, stopDraw } from './mapView.js';
import { makeDraggable } from './draggable.js';
import { t, setLang, getLang, applyStatic, locale } from './i18n.js';
// exportOrder pulls in xlsx + jspdf (~350 KB) — loaded lazily on first export so it stays out of
// the initial bundle (faster first paint / first search).
const loadExport = () => import('./exportOrder.js');
import { correspondingSubsegments, INCOME_MIN, INCOME_MAX } from './dividualMapping.js';
import { initDualRange } from './dualRange.js';

// Dividuals catalogue (from /dividuals/attributes), flattened by subsegment code.
let catalogueSegments = [];
const catalogueByCode = new Map();
let lastPickerAny = false; // whether the pickers last produced a correspondence
let lastMatched = null;    // the last corresponding list (null = full catalogue), for re-rendering
let ageRangeCtl = null;    // dual-range handle, so a restored selection can move the thumbs
let incomeRangeCtl = null; // same for the income index slider
let dividualsReady = null; // promise: catalogue loaded (restore waits for it)

// Cache pc4 -> display name to avoid repeat geo/search calls.
const pcNameCache = new Map();

// Privacy: minimum selection size before exact numbers are shown.
// TODO(privacy): replace with the real rule from The Wave/JM (03-open-vragen Q4).
const MIN_ADDRESSES_FOR_EXACT = 200;

let recountTimer = null;
let fallbackToastShown = false; // tell the user once per session that counts fell back to GIS

// ---------- helpers ----------
const $ = (id) => document.getElementById(id);
const fmt = (n) => (Number.isFinite(n) ? n.toLocaleString(locale()) : '0');

// Every postcode the selection touches: the whole-postcode set plus each sector's pc4.
function selectedPostcodes() {
  const set = new Set(state.pcUnits); // DTM postcodes (radius/drive-time) — counted immediately
  state.sectors.forEach(s => { if (s.postcode) set.add(String(s.postcode)); });
  return [...set];
}

// Counting unit = the zones we ask The Wave (or GIS) for.
//   SECTOR mode: the selection consists of real sectors only (click, draw, Distritool "Wijk"
//   import) → one zone per NIS9, so the reach figures cover exactly the drawn area.
//   POSTCODE mode: whole postcodes are in play (radius, drive-time, municipality / province,
//   postcode import, offline "PC" placeholders) or the selection is huge → one zone per postcode.
const SECTOR_COUNT_CAP = 600; // above this, per-sector calls get slow; fall back to postcodes
function countUnits() {
  const sectors = [...state.sectors.values()];
  const real = sectors.filter(s => !String(s.nis9).startsWith('PC'));
  const sectorMode = state.pcUnits.size === 0 && real.length > 0 &&
    real.length === sectors.length && real.length <= SECTOR_COUNT_CAP;
  if (sectorMode) {
    return {
      mode: 'sector',
      units: real.map(s => ({ id: s.nis9, zoneType: 'sector', zone: nis9ToWaveZone(s.nis9),
        postcode: s.postcode ? String(s.postcode) : null, totalHH: s.totalHH || 0 }))
    };
  }
  return { mode: 'postcode', units: selectedPostcodes().map(pc => ({ id: pc, zoneType: 'zip', zone: pc, postcode: pc, totalHH: 0 })) };
}

// Count units (keys of state.counts) whose target penetration is below the slider threshold.
// No Dividuals selected => no segment filter => every household qualifies (target=total,
// 100%) => nothing drops at any threshold.
// Test hook (like window.__stateCodes in mapView.js): per sector its count unit and target %.
window.__sectorPct = () => [...state.sectors.entries()].map(([k, s]) => {
  return [k, s.postcode || null, sectorPctOf(s), s.totalHH || 0, state.sectorPct.has(k) ? 'sector' : 'unit'];
});
window.__countsLoading = () => !!state.loadingCounts;

// Sectors that fall away: each on its own target % (Sevenn per sector), or its count unit's %
// where Sevenn has no figure for it yet.
function droppedSectors() {
  const dropped = new Set();
  if (state.dividuals.size === 0) return dropped;
  const pct = state.minTargetPct;
  state.sectors.forEach((s, nis9) => {
    const p = sectorPctOf(s);
    if (p != null && p < pct) dropped.add(nis9);
  });
  return dropped;
}

// Per count unit, the share of its TARGET addresses (addresses × target %) that survives
// (1 = untouched, 0 = gone). Scales The Wave's "after" figures when one postcode holds both kept
// and dropped sectors: the kept sectors are the ones dense in the target group.
function unitKeptShare() {
  const ds = droppedSectors();
  const acc = new Map();
  state.sectors.forEach((s, nis9) => {
    const k = countKeyOf(s); if (!k) return;
    const a = acc.get(String(k)) || { all: 0, kept: 0, n: 0, nk: 0 };
    const p = sectorPctOf(s);
    const hh = (s.totalHH || 0) * (p == null ? 1 : p / 100);
    a.all += hh; a.n++; if (!ds.has(nis9)) { a.kept += hh; a.nk++; }
    acc.set(String(k), a);
  });
  const share = new Map();
  let gAll = 0, gKept = 0;
  acc.forEach((a, k) => { share.set(k, a.all > 0 ? a.kept / a.all : a.nk / a.n); gAll += a.all; gKept += a.kept; });
  // Count units no sector points to. Sevenn's pc4 layer gives a whole municipality one postcode
  // (all of Gent → 9000) while The Wave counts its real postcodes, so most of them have no sectors:
  // they take the selection-wide surviving share. Offline "PC" placeholders keep their own %.
  if (state.dividuals.size > 0) state.counts.forEach((c, k) => {
    if (share.has(String(k))) return;
    if (state.pcUnits.has(String(k)) || gAll === 0) share.set(String(k), c.pct < state.minTargetPct ? 0 : 1);
    else share.set(String(k), gKept / gAll);
  });
  return share;
}

// Count units that fall away completely.
function droppedUnits() {
  const out = new Set();
  unitKeptShare().forEach((v, k) => { if (v === 0) out.add(k); });
  return out;
}

// Postcodes that survive / fall away, for the order document. In sector mode a postcode
// survives as soon as one of its sectors does.
function postcodeSplit() {
  const all = selectedPostcodes();
  const ds = droppedSectors(), du = droppedUnits();
  const alive = new Set();
  state.sectors.forEach((s, nis9) => { if (s.postcode && !ds.has(nis9)) alive.add(String(s.postcode)); });
  state.pcUnits.forEach(pc => { if (!du.has(String(pc))) alive.add(String(pc)); });
  return {
    kept: all.filter(pc => alive.has(String(pc))).sort(),
    dropped: all.filter(pc => !alive.has(String(pc))).sort()
  };
}

// ---------- rendering ----------
// Collapse state persists across re-renders (renderTable runs on every count update).
// Provinces default EXPANDED (present = collapsed); municipalities default COLLAPSED
// (present = expanded) — so a long list opens as a compact province→municipality overview.
const collapsedProvs = new Set();
const expandedMunis = new Set();
const PROV_ORDER = ['Antwerpen', 'Vlaams-Brabant', 'Brussel', 'Waals-Brabant', 'West-Vlaanderen',
  'Oost-Vlaanderen', 'Henegouwen', 'Limburg', 'Luik', 'Namen', 'Luxemburg', 'Onbekend'];

// Belgian pc4 -> province by well-known numeric ranges.
function provinceForPostcode(pc) {
  const n = parseInt(pc, 10);
  if (isNaN(n)) return 'Onbekend';
  if (n < 1300) return 'Brussel';
  if (n < 1500) return 'Waals-Brabant';
  if (n < 2000) return 'Vlaams-Brabant';
  if (n < 3000) return 'Antwerpen';
  if (n < 3500) return 'Vlaams-Brabant';
  if (n < 4000) return 'Limburg';
  if (n < 5000) return 'Luik';
  if (n < 6000) return 'Namen';
  if (n < 6600) return 'Henegouwen';
  if (n < 7000) return 'Luxemburg';
  if (n < 8000) return 'Henegouwen';
  if (n < 9000) return 'West-Vlaanderen';
  return 'Oost-Vlaanderen';
}

// Derive province + municipality straight from the sector's NIS9 code (first 5 digits = NIS5
// municipality) — immediate and reliable. The pc4 bridge is async/slow, so deriving province
// from the postcode left not-yet-resolved sectors stranded under "Onbekend".
function nis5Of(s) {
  const code = String(s.nis9 || '');
  return /^\d{5}/.test(code) ? code.slice(0, 5) : null;
}
function provinceForNis5(nis5) {
  const a = parseInt(nis5.slice(0, 2), 10); // arrondissement
  if ([11, 12, 13].includes(a)) return 'Antwerpen';
  if (a === 21) return 'Brussel';
  if ([23, 24].includes(a)) return 'Vlaams-Brabant';
  if (a === 25) return 'Waals-Brabant';
  if (a >= 31 && a <= 38) return 'West-Vlaanderen';
  if (a >= 41 && a <= 46) return 'Oost-Vlaanderen';
  if (a >= 51 && a <= 57) return 'Henegouwen';
  if (a >= 61 && a <= 64) return 'Luik';
  if (a >= 71 && a <= 73) return 'Limburg';
  if (a >= 81 && a <= 85) return 'Luxemburg';
  if (a >= 91 && a <= 93) return 'Namen';
  return 'Onbekend';
}
function provinceOf(s) {
  const n5 = nis5Of(s);
  return n5 ? provinceForNis5(n5) : provinceForPostcode(s.postcode);
}
function muniKeyOf(s) {
  const n5 = nis5Of(s);
  return n5 || (s.postcode ? `pc${s.postcode}` : '?');
}
const nis5NameCache = new Map(); // nis5 -> resolved gemeente name (string) or a pending promise
function muniNameOf(s) {
  if (s.municipality && s.municipality !== '…') return s.municipality;
  const n5 = nis5Of(s);
  if (n5) {
    const c = nis5NameCache.get(n5);
    return typeof c === 'string' ? c : '…';
  }
  return s.postcode ? `pc ${s.postcode}` : '…';
}

// Fill in real gemeente names (via the Councils WFS, one lookup per municipality, cached)
// for NIS5 groups that don't yet have a name, then re-render the table.
let muniNameRerenderTimer = null;
function ensureMuniNames() {
  // Look up each NIS5's name at an INTERIOR point of its biggest sector (by addresses). A point
  // inside a sector is inside the municipality; the mean of the sectors' extent centres is not
  // (a scattered Distritool zone along the Scheldt averaged out to "Antwerpen"), and an
  // arbitrary border sector's extent centre can sit in the neighbour too.
  const best = new Map(); // nis5 -> the sector to probe
  state.sectors.forEach((s) => {
    const n5 = nis5Of(s);
    if (!n5 || nis5NameCache.has(n5)) return;
    if (s.municipality && s.municipality !== '…') return;
    if (s.lng == null || s.lat == null) return;
    const cur = best.get(n5);
    if (!cur || (s.totalHH || 0) > (cur.totalHH || 0)) best.set(n5, s);
  });
  best.forEach((s, n5) => {
    const [lng, lat] = (s.feature && interiorPointOf(s.feature)) || [s.lng, s.lat];
    const p = councilNameAt(lng, lat).then((name) => {
      nis5NameCache.set(n5, name || `Gemeente ${n5}`);
      clearTimeout(muniNameRerenderTimer);
      muniNameRerenderTimer = setTimeout(() => renderTable(), 150);
    });
    nis5NameCache.set(n5, p); // mark pending so concurrent renders don't refetch
  });
}

function renderTable() {
  const tbody = $('selection-table-body');
  if (state.sectors.size === 0) {
    tbody.innerHTML = `<tr class="empty-row"><td colspan="4">${t('table.empty')}</td></tr>`;
    return;
  }
  ensureMuniNames();
  const dropped = droppedSectors();
  const caret = (open) => `<span class="tw-caret">${open ? '▾' : '▸'}</span>`;
  // Letterboxes per row: bpost's own total for that group, as if it were ordered on its own
  // (rows don't always add up exactly to the total: bpost rounds can straddle two rows).
  const boxCell = (keys, bold) => {
    const v = bpostTotalOf(keys);
    const txt = v === undefined ? '<span class="skeleton-bar"></span>' : (v === null || v === '') ? '—' : fmt(v);
    return `<td class="tw-hh">${bold && v !== undefined ? `<b>${txt}</b>` : txt}</td>`;
  };

  // province -> municipality -> [sectors]
  const tree = new Map();
  let grandN = 0;
  state.sectors.forEach((s, nis9) => {
    const prov = provinceOf(s);
    const mkey = muniKeyOf(s);
    if (!tree.has(prov)) tree.set(prov, new Map());
    const munis = tree.get(prov);
    if (!munis.has(mkey)) munis.set(mkey, { name: muniNameOf(s), list: [] });
    const g = munis.get(mkey);
    g.list.push({ nis9, ...s });
    // Prefer a real resolved municipality name over the "Gemeente <nis5>" placeholder.
    if (s.municipality && s.municipality !== '…' &&
        (g.name.startsWith('Gemeente ') || g.name.startsWith('pc '))) g.name = s.municipality;
    grandN++;
  });

  const provNames = [...tree.keys()].sort((a, b) => {
    const ia = PROV_ORDER.indexOf(a), ib = PROV_ORDER.indexOf(b);
    return (ia < 0 ? 99 : ia) - (ib < 0 ? 99 : ib);
  });

  const rows = [];
  provNames.forEach(prov => {
    const munis = tree.get(prov);
    const provKeys = [];
    munis.forEach(g => g.list.forEach(s => provKeys.push(s.nis9)));
    const provN = provKeys.length;
    const provOpen = !collapsedProvs.has(prov);
    rows.push(
      `<tr class="grp grp-prov" data-prov="${prov}">` +
      `<td colspan="2">${caret(provOpen)}<b>${prov}</b> <span class="tw-sub">${munis.size} ${t('table.muni')} · ${provN} ${t('table.sect')}</span></td>` +
      boxCell(provKeys) +
      `<td><button class="locate-btn" data-loc="prov:${prov}" title="${t('table.zoom')}">📍</button>` +
      `<button class="remove-prov" data-prov="${prov}" title="${t('table.removeAll', { name: prov })}">✕</button></td></tr>`);
    if (!provOpen) return;

    [...munis.entries()].sort((a, b) => a[1].name.localeCompare(b[1].name)).forEach(([mkey, g]) => {
      const key = `${prov}|${mkey}`;
      const mOpen = expandedMunis.has(key);
      rows.push(
        `<tr class="grp grp-muni" data-muni="${key}">` +
        `<td colspan="2" class="tw-indent1">${caret(mOpen)}${g.name} <span class="tw-sub">${g.list.length} ${t('table.sect')}</span></td>` +
        boxCell(g.list.map(s => s.nis9)) +
        `<td><button class="locate-btn" data-loc="muni:${key}" title="${t('table.zoom')}">📍</button>` +
        `<button class="remove-muni" data-muni="${key}" title="${t('table.removeAll', { name: g.name })}">✕</button></td></tr>`);
      if (!mOpen) return;
      g.list.forEach(s => {
        rows.push(
          `<tr class="sec${dropped.has(s.nis9) ? ' dropped' : ''}">` +
          `<td class="tw-indent2">${s.nis9}</td><td class="tw-sub">${g.name}</td>` +
          boxCell([s.nis9]) +
          `<td><button class="locate-btn" data-loc="sec:${s.nis9}" title="${t('table.zoom')}">📍</button>` +
          `<button class="remove-btn" data-nis9="${s.nis9}" title="${t('table.remove')}">✕</button></td></tr>`);
      });
    });
  });

  rows.push(
    `<tr class="grp-total"><td colspan="2"><b>${t('table.total')}</b> <span class="tw-sub">${grandN} ${t('table.sect')}</span></td>` +
    `${boxCell([...state.sectors.keys()], true)}<td></td></tr>`);

  tbody.innerHTML = rows.join('');

  const rerender = () => renderTable();
  tbody.querySelectorAll('.grp-prov').forEach(tr => tr.addEventListener('click', (e) => {
    if (e.target.closest('button')) return;
    const p = tr.dataset.prov;
    collapsedProvs.has(p) ? collapsedProvs.delete(p) : collapsedProvs.add(p);
    rerender();
  }));
  tbody.querySelectorAll('.grp-muni').forEach(tr => tr.addEventListener('click', (e) => {
    if (e.target.closest('button')) return;
    const k = tr.dataset.muni;
    expandedMunis.has(k) ? expandedMunis.delete(k) : expandedMunis.add(k);
    rerender();
  }));
  // Remove all sectors of a group AND the postcodes they contributed to state.pcUnits (so the
  // counts drop too — admin-area selections add whole-area postcodes to pcUnits up front). A
  // postcode only leaves pcUnits if no surviving sector still references it.
  const removeSectorsWhere = (pred) => {
    const gone = new Set();
    state.sectors.forEach((s, nis9) => {
      if (pred(s, nis9)) { if (s.postcode) gone.add(String(s.postcode)); state.sectors.delete(nis9); }
    });
    gone.forEach(pc => {
      let stillUsed = false;
      state.sectors.forEach(s => { if (String(s.postcode) === pc) stillUsed = true; });
      if (!stillUsed) state.pcUnits.delete(pc);
    });
    emit('sectors-changed');
    scheduleRecount();
  };
  tbody.querySelectorAll('.remove-prov').forEach(btn => btn.addEventListener('click', (e) => {
    e.stopPropagation();
    const prov = btn.dataset.prov;
    // Also drop any province postcodes that have no sector (e.g. failed to load) via the pc→province map.
    [...state.pcUnits].forEach(pc => { if (provinceForPostcode(pc) === prov) state.pcUnits.delete(pc); });
    removeSectorsWhere(s => provinceOf(s) === prov);
    toast(t('st.removed', { name: prov }), 'info');
  }));
  tbody.querySelectorAll('.remove-muni').forEach(btn => btn.addEventListener('click', (e) => {
    e.stopPropagation();
    const [prov, mkey] = btn.dataset.muni.split('|');
    removeSectorsWhere(s => provinceOf(s) === prov && muniKeyOf(s) === mkey);
  }));
  tbody.querySelectorAll('.remove-btn').forEach(btn => btn.addEventListener('click', (e) => {
    e.stopPropagation();
    removeSectorsWhere((s, nis9) => nis9 === btn.dataset.nis9);
  }));
  tbody.querySelectorAll('.locate-btn').forEach(btn => btn.addEventListener('click', (e) => {
    e.stopPropagation();
    const loc = btn.dataset.loc || '';
    const type = loc.slice(0, loc.indexOf(':'));
    const rest = loc.slice(loc.indexOf(':') + 1);
    const feats = [];
    if (type === 'sec') {
      const s = state.sectors.get(rest);
      if (s && s.feature) feats.push(s.feature);
    } else if (type === 'prov') {
      state.sectors.forEach(s => { if (provinceOf(s) === rest && s.feature) feats.push(s.feature); });
    } else if (type === 'muni') {
      const [prov, mkey] = rest.split('|');
      state.sectors.forEach(s => { if (provinceOf(s) === prov && muniKeyOf(s) === mkey && s.feature) feats.push(s.feature); });
    }
    if (feats.length) focusFeatures(feats);
    else setStatus(t('st.noGeom'));
  }));
}

function renderMap() {
  refreshStyles(droppedSectors());
}

// Threshold profile under the slider: for every threshold 0..100, the share of the selection's
// households that would survive (sectors whose unit's penetration >= threshold). Drawn as a
// colour band (green = all, grey = none) plus the three breakpoints in words — so a user sees
// "from 46% nothing is left" before dragging. hhIn = null hides it (no segmentation / loading).
let lastProfileSig = '';
let lastHH = { in: 0, after: 0 };
let lastBp = { all: 0, kept: 0, exact: true };
function renderPctProfile(hhIn) {
  const bar = $('pct-profile'), txt = $('pct-breakpoints');
  if (!bar || !txt) return;
  if (!hhIn) { bar.hidden = true; txt.hidden = true; lastProfileSig = ''; return; }
  // Cache key covers the penetration values themselves: a Dividual click re-renders with the
  // old counts first (same sizes, new dividuals) and the fresh counts arrive later.
  let chk = 0;
  state.counts.forEach((c) => { chk += Math.round((Number(c?.pct) || 0) * 1000); });
  state.sectorPct.forEach((v) => { chk += Math.round(v * 1000); });
  const sig = `${state.countUnit}|${state.counts.size}|${chk}|${state.sectors.size}|${hhIn}`;
  if (sig !== lastProfileSig) {
    // Households per integer percentage (sector pct floored), then a cumulative "survives at >= t".
    const byPct = new Array(101).fill(0);
    state.sectors.forEach((s) => {
      const sp = sectorPctOf(s);
      if (sp == null) return;
      const p = Math.max(0, Math.min(100, Math.floor(Number(sp) || 0)));
      byPct[p] += s.totalHH || 0;
    });
    const alive = new Array(101).fill(0);
    let acc = 0;
    for (let t = 100; t >= 0; t--) { acc += byPct[t]; alive[t] = acc; }
    const share = alive.map(v => v / hhIn);
    // Colour stops: green at 100 % surviving, pale at 0.
    const stops = share.map((f, t) => {
      const g = Math.round(f * 100);
      return `color-mix(in srgb, #2e9e5b ${g}%, #e5e7eb) ${t}%`;
    });
    bar.style.background = `linear-gradient(to right, ${stops.join(', ')})`;
    let a = 0; while (a < 100 && share[a + 1] >= 0.999) a++;
    let h = 0; while (h < 100 && share[h] > 0.5) h++;
    let z = 0; while (z < 100 && share[z] > 0) z++;
    bar.dataset.alive = JSON.stringify(alive);
    // One sentence per real breakpoint: skip "half" when it coincides with "nothing".
    const parts = [t('profile.all', { a })];
    if (h < z) parts.push(t('profile.half', { h }));
    parts.push(t('profile.none', { z }));
    txt.innerHTML = parts.map(x => `<div>${x}</div>`).join('');
    lastProfileSig = sig;
  }
  try {
    const alive = JSON.parse(bar.dataset.alive || '[]');
    const p = state.minTargetPct, n = alive[p] || 0, s = n / hhIn;
    bar.title = t('profile.title', { p, n: fmt(Math.round(s * (lastBp.all || 0))), s: Math.round(s * 100) });
  } catch { /* ignore */ }
  bar.hidden = false; txt.hidden = false;
}

function updateSelectionPanel() {
  const du = droppedUnits();
  const dropped = droppedSectors();

  // HOUSEHOLDS (base) = ALL postal addresses in the selection, from the sector layer
  // (u108264Aantal_adres). Segmentation drops whole zones: "after" = Σ households of the sectors
  // whose postcode survives the min-% slider (same dropped set the map greys). This is the true
  // audience size (~800k for a province) — NOT The Wave dm, which is only the named-deliverable slice.
  let hhIn = 0, hhAfter = 0;
  state.sectors.forEach((s, nis9) => {
    hhIn += s.totalHH || 0;
    if (!dropped.has(nis9)) hhAfter += s.totalHH || 0;
  });

  // REACH (The Wave, per count unit): "in selection" = everyone in the zone reachable by addressed
  // post (dm = name known), e-mail (em) and phone (sms). "target" = only the people in the chosen
  // Dividuals, in the zones that survive the threshold — the same figure Instadata shows for the
  // same selection (checked 2026-10-15: 2,398 dm for Selectie 4 with E+G25+H, Dutch).
  let dmIn = 0, dmAfter = 0, emIn = 0, emAfter = 0, phoneIn = 0, phoneAfter = 0;
  const keptShare = state.dividuals.size > 0 ? unitKeptShare() : null;
  state.counts.forEach((c, key) => {
    dmIn += c.total; emIn += c.emailTotal; phoneIn += c.phoneTotal || 0;
    const f = keptShare ? (keptShare.get(String(key)) ?? 1) : 1;
    dmAfter += c.target * f; emAfter += c.emailTarget * f; phoneAfter += (c.phoneTarget || 0) * f;
  });
  dmAfter = Math.round(dmAfter); emAfter = Math.round(emAfter); phoneAfter = Math.round(phoneAfter);

  // Privacy floor applies to the real selection size (households).
  const privacyEl = $('privacy-note');
  const belowMin = state.sectors.size > 0 && hhIn > 0 && hhIn < MIN_ADDRESSES_FOR_EXACT;
  if (belowMin) {
    privacyEl.hidden = false;
    privacyEl.textContent =
      t('privacy', { n: MIN_ADDRESSES_FOR_EXACT });
  } else {
    privacyEl.hidden = true;
  }

  const zoneFilter = state.dividuals.size > 0;                 // drops zones, moves 'Adressen'
  const reachFilter = zoneFilter || !!state.waveLang;          // moves the Wave rows only
  const countsReady = !state.loadingCounts && state.counts.size > 0;
  const hasSel = state.sectors.size > 0 || state.pcUnits.size > 0;
  // Households come straight from the sector layer → ready as soon as sectors load. Reach numbers
  // need the live Wave counts. Drops (the "after" columns under a filter) need the counts too.
  const hhReady = !hasSel || hhIn > 0;
  const reachReady = !hasSel || countsReady;
  const afterReady = !hasSel || countsReady;
  const cell = (id, ready, v) => {
    const el = $(id);
    if (!el) return;
    if (belowMin) el.textContent = '—';
    else if (ready) el.textContent = fmt(v);
    else el.innerHTML = '<span class="skeleton-bar"></span>';
  };
  renderPctProfile(!zoneFilter || !afterReady || belowMin ? null : hhIn);
  cell('dm-in', reachReady, dmIn);
  cell('em-in', reachReady, emIn);
  cell('phone-in', reachReady, phoneIn);
  // GIS addresses are no longer shown: they only drive the privacy floor, the profile band and
  // the pro-rata split of bpost's sub-municipality figures. The panel shows bpost boxes only.
  lastHH = { in: hhIn, after: zoneFilter ? hhAfter : hhIn };
  // bpost letterboxes: straight sums over the sectors (no Wave dependency). Shown as soon as
  // every sector in the selection has an answer; a skeleton while bpost is still counting.
  {
    const bp = bpostSums(dropped);
    lastBp = bp;
    const bpReady = !hasSel || (bp.missing === 0 && !bpostLoading());
    cell('bp-in', bpReady, bp.all);
    cell('bp-after', zoneFilter ? (bpReady && afterReady) : bpReady, zoneFilter ? bp.kept : bp.all);
    if (bpReady && hasSel && bp.failed && !belowMin) ['bp-in', 'bp-after'].forEach((id) => { const el = $(id); if (el) el.textContent = '—'; });
    const row = $('bp-in')?.closest('tr');
    if (row) row.title = (bp.failed ? t('counts.boxesFailed') : t('counts.boxesTitle')) + (bp.unknown ? ' ' + t('counts.boxesUnknown', { n: bp.unknown }) : '');
  }
  cell('dm-after', reachFilter ? afterReady : reachReady, reachFilter ? dmAfter : dmIn);
  cell('em-after', reachFilter ? afterReady : reachReady, reachFilter ? emAfter : emIn);
  cell('phone-after', reachFilter ? afterReady : reachReady, reachFilter ? phoneAfter : phoneIn);
}

function refreshUI() {
  renderTable();
  renderMap();
  updateSelectionPanel();
}

// ---------- data flow ----------
// Resolve a lat/lng to a pc4 postcode via the GIS proxy. The live /geo/postcode-at is
// ~1.5s/call and serializes, so cache by a ~1 km coordinate cell: clustered drawn sectors
// then share one lookup (demo approximation — boundary sectors may inherit a neighbour's
// pc; acceptable at this granularity). In-flight promises are shared too.
const pcAtCache = new Map();
// cellDeg controls the cache granularity: 0.01 (~1 km) for clicks/draws; a coarser value
// (e.g. 0.02 ~2 km) for bulk radius/drive-time selections to cut network calls.
async function resolvePostcode(lat, lng, cellDeg = 0.01) {
  if (lat == null || lng == null) return null;
  const q = (v) => (Math.round(v / cellDeg) * cellDeg).toFixed(3);
  const key = `${q(lat)},${q(lng)}`;
  if (pcAtCache.has(key)) return pcAtCache.get(key);
  const promise = (async () => {
    try {
      const r = await api.postcodeAt(lat, lng, 'pc4');
      return r && (r.code || r.postcode) ? String(r.code || r.postcode) : null;
    } catch { return null; }
  })();
  pcAtCache.set(key, promise);
  const val = await promise;
  pcAtCache.set(key, val); // replace promise with resolved value
  return val;
}

// Resolve a list with bounded concurrency; cache hits return instantly.
async function runPool(items, worker, concurrency = 8, onProgress) {
  let idx = 0, done = 0;
  const run = async () => {
    while (idx < items.length) {
      const i = idx++;
      await worker(items[i]);
      done++;
      if (onProgress) onProgress(done, items.length);
    }
  };
  await Promise.all(Array.from({ length: Math.min(concurrency, items.length) }, run));
}

// Add many sectors (with geometry) at once: render immediately, then resolve each sector's
// pc4 in a bounded pool (coarse cache), optionally keeping only those in `keepPcs`, then
// recount. This is what makes radius / drive-time selections show sectors on the map.
// cellDeg: postcode-resolution cache granularity. Radius uses ~3 km (fast, no filtering);
// drive-time uses a finer ~1.5 km so the keepPcs membership test is accurate at boundaries.
async function addBulkSectors(infos, { keepPcs = null, label = '', cellDeg = 0.03, resolvePostcodes = true } = {}) {
  const added = [];
  infos.forEach((info) => {
    if (state.sectors.has(info.nis9)) return;
    state.sectors.set(info.nis9, { nis9: info.nis9, postcode: null, municipality: '…', totalHH: info.totalHH, feature: info.feature, lat: info.lat, lng: info.lng });
    added.push(info);
  });
  if (!added.length) { setStatus(t('st.noNewSectors', { label })); return 0; }
  emit('sectors-changed');
  refreshUI();          // candidate sectors render on the map right away (geometry present)
  // Zoom to the JUST-added area, not the whole accumulated selection.
  {
    let mnLng = Infinity, mnLat = Infinity, mxLng = -Infinity, mxLat = -Infinity;
    added.forEach(i => {
      if (i.lng < mnLng) mnLng = i.lng; if (i.lat < mnLat) mnLat = i.lat;
      if (i.lng > mxLng) mxLng = i.lng; if (i.lat > mxLat) mxLat = i.lat;
    });
    if (isFinite(mnLng)) fitToBBox([mnLng, mnLat, mxLng, mxLat], 60);
  }

  if (!resolvePostcodes) {
    // Whole province/municipality: thousands of sectors — skip the slow per-sector pc lookup.
    // Counts come from the whole-area postcode set (state.pcUnits) the caller already added.
    scheduleRecount();
    setStatus(t('st.sectorsShown', { label, n: added.length }));
    toast(t('st.wholeArea', { label }), 'success');
    return added.length;
  }

  // Resolve postcodes (bounded pool) and drop out-of-set sectors as we go, so the map
  // settles from the candidate area to the truly reachable sectors.
  let removed = 0;
  await runPool(added, async (info) => {
    const pc = await resolvePostcode(info.lat, info.lng, cellDeg);
    const s = state.sectors.get(info.nis9);
    if (!s) return;
    if (keepPcs && (!pc || !keepPcs.has(String(pc)))) {
      state.sectors.delete(info.nis9); // not in the drive-time postcode set
      removed++;
      if (removed % 25 === 0) refreshUI(); // periodically settle the map
    } else {
      s.postcode = pc;
      s.municipality = pc ? await nameForPostcode(pc) : '';
    }
  }, 8, (done, total) => { setStatus(t('st.resolving', { done, total })); mapLoad('resolve', t('st.resolvingZones', { done, total })); });
  mapLoad('resolve', null);

  emit('sectors-changed');
  refreshUI();
  scheduleRecount();
  const kept = [...state.sectors.values()].filter(s => s.feature).length;
  setStatus(t('st.sectorsSelected', { label, n: kept }));
  toast(t('st.zonesSelected', { label, n: kept }), 'success');
  return added.length;
}
async function nameForPostcode(pc) {
  if (!pc) return '';
  if (pcNameCache.has(pc)) return pcNameCache.get(pc);
  let name = pc;
  try {
    const s = await api.geoSearch(pc);
    const first = (s.results || [])[0];
    if (first) name = first.label || first.name || pc;
  } catch { /* keep pc as name */ }
  pcNameCache.set(pc, name);
  return name;
}

// pc4 -> {lat,lng} centroid. Static table first (GeoNames BE, bundled: instant, no API call),
// geo/search only for a code the table doesn't know. geo/search takes ~3 s per call and the GIS
// serialises them, so a province's 64 postcodes took 2-3 minutes — until then no sector had a
// postcode, so nothing greyed or dropped under the threshold and 'Adressen' never moved.
const pcCentroidCache = new Map();
async function pcCentroid(pc) {
  const key = String(pc);
  if (pcCentroidCache.has(key)) return pcCentroidCache.get(key);
  let c = null;
  const st = PC_CENTROIDS[key];
  if (st) { c = { lat: st[0], lng: st[1] }; pcCentroidCache.set(key, c); return c; }
  try {
    const r = await api.geoSearch(key);
    const f = (r.results || [])[0];
    const lat = f?.center?.lat ?? f?.latitude, lng = f?.center?.lng ?? f?.longitude;
    if (lat != null && lng != null) c = { lat, lng };
  } catch { /* ignore */ }
  pcCentroidCache.set(key, c);
  return c;
}

// Give every sector its NEAREST postcode (Voronoi) so the map can shade/grey it by segmentation.
// Cost = one geo/search per postcode (a handful per area), not per sector — instant even for a
// whole province, unlike per-sector /geo/postcode-at (~1.5s each, minutes for thousands).
async function assignPostcodesByNearest(infos, pcs) {
  const uniq = [...new Set((pcs || []).map(String))];
  if (!uniq.length) return;
  // Fetch centroids with a bounded pool (not a big Promise.all burst) so a cold isolate / GIS
  // auth warm-up doesn't get hammered by dozens of simultaneous requests (→ 502s). Cached per pc.
  const cents = [];
  await runPool(uniq, async (pc) => {
    const c = await pcCentroid(pc);
    if (c) cents.push({ pc, lat: c.lat, lng: c.lng });
  }, 6);
  if (!cents.length) return;
  let n = 0;
  infos.forEach((info) => {
    const s = state.sectors.get(info.nis9);
    if (!s || s.postcode || info.lat == null || info.lng == null) return;
    let best = Infinity, bpc = null;
    for (const c of cents) {
      const d = (c.lng - info.lng) ** 2 + (c.lat - info.lat) ** 2;
      if (d < best) { best = d; bpc = c.pc; }
    }
    if (bpc) { s.postcode = bpc; n++; }
  });
  if (n) { emit('sectors-changed'); refreshUI(); scheduleRecount(); }
}

// Click-to-select a real sector polygon on the map. `info` from mapView.
async function onSectorToggle(info) {
  const code = info.nis9;
  if (state.sectors.has(code)) {
    state.sectors.delete(code);
    emit('sectors-changed');
    refreshUI();
    scheduleRecount();
    return;
  }
  // Add immediately (postcode resolves asynchronously), then refine.
  state.sectors.set(code, { nis9: code, postcode: info.postcode || null, municipality: '…', totalHH: info.totalHH, feature: info.feature, lat: info.lat, lng: info.lng });
  emit('sectors-changed');
  refreshUI();

  const pc = info.postcode || await resolvePostcode(info.lat, info.lng);
  const s = state.sectors.get(code);
  if (s) { s.postcode = pc; s.municipality = pc ? await nameForPostcode(pc) : ''; }
  emit('sectors-changed');
  refreshUI();
  scheduleRecount();
}

// Draw-to-select: add every sector whose centroid fell inside the drawn shape, then
// resolve each new sector's pc4 in parallel and recount once.
async function onDrawSelect(infos) {
  const added = [];
  infos.forEach((info) => {
    if (state.sectors.has(info.nis9)) return;
    state.sectors.set(info.nis9, { nis9: info.nis9, postcode: info.postcode || null, municipality: '…', totalHH: info.totalHH, feature: info.feature, lat: info.lat, lng: info.lng });
    added.push(info);
  });
  if (!added.length) { setStatus(t('st.noneInShape')); return; }
  emit('sectors-changed');
  refreshUI();
  setStatus(t('st.drawnResolving', { n: added.length }));

  // Bounded pool (NOT an unbounded Promise.all — a big drawn shape has hundreds of sectors and
  // would fire hundreds of simultaneous postcode-at + geo/search calls → GIS 502s). The coarse
  // grid cache (cellDeg 0.02 ≈ 2 km) makes neighbouring sectors share one postcode-at lookup, so
  // it's a few dozen calls, not one per sector.
  await runPool(added, async (info) => {
    const pc = info.postcode || await resolvePostcode(info.lat, info.lng, 0.02);
    const s = state.sectors.get(info.nis9);
    if (s) s.postcode = pc; // municipality name comes from ensureMuniNames (per NIS5) — no per-sector geo/search
  }, 8, (done, total) => { setStatus(t('st.resolving', { done, total })); mapLoad('resolve', t('st.resolvingZones', { done, total })); });
  mapLoad('resolve', null);
  emit('sectors-changed');
  refreshUI();
  scheduleRecount();
  setStatus(t('st.drawn', { n: added.length }));
}

// Add sectors to selection (used by the search-box "Add" flow); postcode preset.
function addSectors(sectorList, postcode, municipality) {
  let added = 0;
  sectorList.forEach(s => {
    if (!s.nis9 || state.sectors.has(s.nis9)) return;
    state.sectors.set(s.nis9, { ...s, postcode: postcode || s.postcode || null, municipality: municipality || '' });
    added++;
  });
  if (added) emit('sectors-changed');
  return added;
}


// Normalise a geo/search result into { label, type, center:{lat,lng}, zoom, pc }.
function normalizeArea(r) {
  if (!r) return null;
  const center = r.center || { lat: r.latitude ?? r.lat, lng: r.longitude ?? r.lng };
  const pc = r.type === 'pc4' ? String(r.name || r.pc4 || r.label || '').match(/\d{4}/)?.[0] : null;
  // area_name for geo/postcodes: prefer the id's last segment (e.g. "4:province:West-Vlaanderen"),
  // else strip a trailing "(XXX)" suffix from the label (e.g. "Brussel (BRU)" -> "Brussel").
  let areaName = (typeof r.id === 'string' && r.id.includes(':')) ? r.id.split(':').pop() : '';
  if (!areaName) areaName = String(r.label || r.name || '').replace(/\s*\([^)]*\)\s*$/, '').trim();
  return { label: r.label || r.name || '', type: r.type || '', areaType: r.type || '', areaName, center, bbox: Array.isArray(r.bbox) ? r.bbox : null, zoom: r.zoom || 13, pc };
}

// Select a WHOLE administrative area (province / municipality / place): all its postcodes for
// the counts, its sectors on the map. Returns true when handled, false when the area is not an
// admin area or GIS returned no postcodes (callers then fall back to scope/centroid behaviour).
// `nis5`: when the caller already knows the municipality's NIS5 (e.g. a Distritool import), the
// sector filter uses it directly instead of the anchor heuristics.
async function addAdminArea(area, { nis5 = null } = {}) {
  const { center } = area;
    if (['province', 'municipality', 'place'].includes(area.areaType) && area.areaName) {
      setStatus(t('st.loadingPostcodes', { label: area.label }));
      let pcs = [], repPc = null;
      try {
        const gp = await api.geoPostcodes({ type: 'pc4', areaType: area.areaType, areaName: area.areaName });
        // Drop institutional 1-address postcodes (e.g. Brussel's 1007/1010/1047 "Europees
        // Parlement", "Europese Unie" …) — they add nothing but clutter the selection/counts.
        const codes = (gp.codes || []).filter(c => (c.addressCount ?? 999) >= 10);
        pcs = [...new Set(codes.map(c => String(c.id)).filter(Boolean))];
        // Representative postcode = the most-addresses one; its centroid sits deep inside the area
        // core → a reliable anchor for the municipality's NIS5, even when the admin centroid lands
        // in a neighbour for a non-contiguous municipality (Stad Brussel's centroid → Schaarbeek).
        const rep = codes.reduce((a, c) => (Number(c.addressCount || 0) > Number((a && a.addressCount) || 0) ? c : a), null);
        repPc = rep ? String(rep.id) : (pcs[0] || null);
      } catch (e) { console.warn('geoPostcodes failed', e); }
      if (pcs.length) {
        // Province / place: count the whole area's postcodes right away. A municipality is counted
        // per SECTOR (see below): exact, and immune to the merged-municipality problem where GIS
        // folds the old postcodes into one (Lievegem → only 9931) so a postcode count would miss
        // Waarschoot and Lovendegem entirely.
        if (area.areaType !== 'municipality') {
          pcs.forEach(p => state.pcUnits.add(p));
          scheduleRecount();
        }
        // Show the WHOLE area on the map (addBulkSectors zooms to the just-added sectors).
        if (area.bbox) {
          try {
            if (area.areaType === 'province') {
              // Province bbox is a rectangle → it clips into neighbouring provinces at the corners.
              // A province == the union of its NIS5s, so filter by NIS9 province (fast + exact).
              const infos = (await fetchSectorsInBBox(area.bbox))
                .filter(s => { const n5 = nis5Of(s); return n5 && provinceForNis5(n5) === area.areaName; });
              await addBulkSectors(infos, { label: area.label, resolvePostcodes: false });
              // Tag each sector with its nearest postcode so the map shades/drops it on segmentation.
              assignPostcodesByNearest(infos, pcs).catch(() => {});
            } else if (area.areaType === 'municipality') {
              // A municipality is one NIS5 — or several since the 2025 mergers: Distritool exports the
              // NEW code (Lokeren+Moerbeke = 46029) while the sector layer still carries the OLD ones
              // (46014, 46010). So: take the hint when the layer knows it, and otherwise collect the
              // NIS5 of the sector containing each of the municipality's postcode centroids. Point-in-
              // sector is exact; "nearest sector centroid" flips to a neighbour near borders
              // (Hasselt → Alken), so that is only the last resort.
              const cand = await fetchSectorsInBBox(area.bbox);
              const candNis5 = new Set(cand.map(nis5Of).filter(Boolean));
              const targets = new Set();
              // Preferred: the municipality's own boundary from the Councils layer, and every
              // sector whose interior point falls inside it. Works for any merger, past or future,
              // without a code table (Lievegem = Lovendegem + Waarschoot + Zomergem, etc.).
              let inside = null;
              try {
                const council = await fetchCouncilGeometry(area.areaName);
                if (council) { inside = sectorsInsideGeometry(cand, council); if (!inside.length) inside = null; }
              } catch { inside = null; }
              if (inside) inside.forEach(s => { const n5 = nis5Of(s); if (n5) targets.add(n5); });
              if (!targets.size && nis5 && candNis5.has(nis5)) targets.add(nis5);
              if (!targets.size) {
                const anchors = [];
                for (const pc of (repPc ? [repPc, ...pcs.filter(p => p !== repPc)] : pcs).slice(0, 12)) {
                  const c = await pcCentroid(pc);
                  if (c) anchors.push(c);
                }
                if (!anchors.length) anchors.push(center);
                await runPool(anchors, async (a) => {
                  const n9 = await sectorNis9At(a.lng, a.lat);
                  if (n9 && /^\d{5}/.test(n9) && candNis5.has(n9.slice(0, 5))) targets.add(n9.slice(0, 5));
                }, 4);
              }
              if (!targets.size) {
                const anchor = (repPc && await pcCentroid(repPc)) || center;
                let best = Infinity, target = null;
                for (const s of cand) {
                  const n5 = nis5Of(s); if (!n5 || s.lng == null || s.lat == null) continue;
                  const d = (s.lng - anchor.lng) ** 2 + (s.lat - anchor.lat) ** 2;
                  if (d < best) { best = d; target = n5; }
                }
                if (target) targets.add(target);
              }
              // We already know this municipality's name — label the group(s) with it directly, so it
              // never depends on a (border-sensitive) councilNameAt lookup.
              if (area.areaName) targets.forEach(t => nis5NameCache.set(t, area.areaName));
              const infos = inside || (targets.size ? cand.filter(s => targets.has(nis5Of(s))) : cand);
              if (!inside) { pcs.forEach(p => state.pcUnits.add(p)); scheduleRecount(); } // boundary unknown: old postcode counting
              await addBulkSectors(infos, { label: area.label, resolvePostcodes: false });
              // Tag each sector with its nearest postcode (GIS share lookups, table grouping) and
              // count: with no pcUnits this selection counts per sector.
              await assignPostcodesByNearest(infos, pcs).catch(() => {});
              scheduleRecount();
            } else {
              // place (deelgemeente): smaller than a NIS5 → keep only sectors whose resolved pc4 is
              // in THIS area's postcode set (the same set we count), on a fine grid for accuracy.
              const infos = await fetchSectorsInBBox(area.bbox);
              await addBulkSectors(infos, { keepPcs: new Set(pcs), label: area.label, cellDeg: 0.008 });
            }
          } catch (e) { console.warn('admin area: sectors not loaded', e); }
        } else {
          const span = area.areaType === 'province' ? 16 : area.areaType === 'municipality' ? 8 : 2.5;
          flyTo(center.lng, center.lat, area.areaType === 'province' ? 9 : area.areaType === 'municipality' ? 11 : 13);
          try { await addBulkSectors(await sectorsInRadius(center.lng, center.lat, span), { label: area.label }); } catch { /* map-only */ }
        }
        setStatus(t('st.postcodesSelected', { label: area.label, n: pcs.length }));
        return true;
      }
    }
  return false;
}

async function addAreaFromInput() {
  const input = $('area-input');
  const q = input.value.trim();
  if (!q) return;
  hideSuggestions();
  const scope = $('scope-select').value; // 'area' | 'radius' | 'drivetime'
  const scopeValue = Number($('scope-value').value) || (scope === 'radius' ? 10 : 20);
  const btn = $('area-add-btn');
  btn.disabled = true; btn.textContent = '…';
  try {
    // Use the picked suggestion if it still matches the input, else resolve free text.
    let area = (selectedArea && selectedArea.label && input.value === selectedArea.label)
      ? selectedArea : null;
    if (!area) {
      const search = await api.geoSearch(q);
      area = normalizeArea((search.results || [])[0]);
    }
    if (!area || area.center.lat == null) { setStatus(t('st.noMatch', { q })); return; }
    const { center } = area;

    // Administrative areas (province / municipality / place) = the WHOLE area's postcodes,
    // regardless of the radius/drive scope (a radius around a province centroid is meaningless).
    if (await addAdminArea(area)) { input.value = ''; selectedArea = null; return; }
    // (no postcodes returned -> fall through to scope/centroid behaviour)

    flyTo(center.lng, center.lat, scope === 'area' ? area.zoom : 10);

    // --- Radius: circle-select real sectors (straight-line distance = a circle) ---
    if (scope === 'radius') {
      const label = t('label.kmAround', { v: scopeValue, label: area.label || q });
      showAreaCircle(center.lng, center.lat, scopeValue);   // visual cue
      // FAST COUNTS: get the postcode set straight from DTM (distance) and count those
      // directly — don't wait on hundreds of per-sector postcode lookups.
      setStatus(t('st.findingWithin', { label }));
      const radiusPcs = [];
      try {
        const resp = await api.dtm({ type: 'distance', value: scopeValue, lat: center.lat, long: center.lng, level: 'pc4', country: 'BE' });
        (resp.data || resp.postcodes || []).forEach(r => { const pc = String(r.postal_code ?? r.code ?? r); if (pc) { state.pcUnits.add(pc); radiusPcs.push(pc); } });
      } catch (e) { console.warn('dtm distance failed', e); }
      scheduleRecount(); // counts appear immediately from the postcode set
      // Load the sector polygons for the map; coloring/threshold refine in the background.
      const infos = await sectorsInRadius(center.lng, center.lat, scopeValue);
      await addBulkSectors(infos, { label, resolvePostcodes: false });
      // Tag sectors with their nearest DTM postcode (Voronoi) instead of one /geo/postcode-at per
      // sector — ~1 call per postcode (~dozens) vs per sector (~hundreds), so no 502 burst.
      assignPostcodesByNearest(infos, radiusPcs).catch(() => {});
      input.value = ''; selectedArea = null;
      return;
    }

    // --- Drive time (accurate): DTM gives the true reachable postcode set. Fetch WFS
    //     sectors across a bbox covering the FULL reach, then keep only those whose resolved
    //     pc4 is in that set (addBulkSectors renders candidates immediately, then drops the
    //     unreachable ones as resolution completes → the true irregular isochrone area). ---
    if (scope === 'drivetime') {
      const label = t('label.minAround', { v: scopeValue, label: area.label || q });
      setStatus(t('st.drivetime', { label }));
      const resp = await api.dtm({ type: 'drivetime', value: scopeValue, lat: center.lat, long: center.lng, level: 'pc4', country: 'BE' });
      const rows = resp.data || resp.postcodes || [];
      const pcs = new Set(rows.map(r => String(r.postal_code ?? r.code ?? r)).filter(Boolean));
      if (!pcs.size) { setStatus(t('st.noPostcodes', { label })); return; }
      // Cover the full reach geographically: bbox must span the furthest reachable postcode
      // by STRAIGHT-LINE distance (road distance over-sizes the bbox). + margin, cap ~50 km.
      const reachKm = Math.min(50, Math.max(8, Math.ceil(Math.max(
        ...rows.map(r => Number(r.distance ?? r.drivedistance ?? 0)), 10) * 1.15)));
      clearAreaCircle(); // no circle — the selection is the postcode-filtered sectors
      // FAST COUNTS: count the reachable postcodes directly, immediately.
      pcs.forEach(pc => state.pcUnits.add(pc));
      scheduleRecount();
      setStatus(t('st.candidates', { km: reachKm, n: pcs.size, label }));
      // Candidates = sectors within the full-reach circle; keepPcs then keeps only those whose
      // resolved pc4 is actually reachable → the true irregular isochrone (not the whole circle).
      const infos = await sectorsInRadius(center.lng, center.lat, reachKm);
      await addBulkSectors(infos, { keepPcs: pcs, label, cellDeg: 0.02 });
      input.value = ''; selectedArea = null;
      return;
    }

    // --- This area only (pc4 / fallback): resolve pc4 + load its sectors from geoserver ---
    let pc = area.pc || await resolvePostcode(center.lat, center.lng);
    const name = area.label || (pc ? await nameForPostcode(pc) : '');
    let sectors = [];
    try { sectors = await sectorsAround(center.lng, center.lat, 1.2); } catch { /* geoserver offline */ }
    let n = addSectors(sectors, pc, name);
    // Fallback (offline / no sectors in view): add the postcode itself as one unit.
    if (n === 0 && pc && !state.sectors.has(`PC${pc}`)) {
      state.sectors.set(`PC${pc}`, { nis9: `PC${pc}`, postcode: pc, municipality: name, totalHH: 0, feature: null });
      emit('sectors-changed');
      n = 1;
    }
    setStatus(n
      ? t('st.addedAround', { n, label: area.label || q, pc: pc ? ` (pc ${pc})` : '' })
      : t('st.noMatchTry', { q }));
    input.value = ''; selectedArea = null;
    refreshUI();
    scheduleRecount();
  } catch (e) {
    console.error(e);
    setStatus(t('st.error', { msg: e.message }));
    toast(t('st.couldNotAdd', { msg: e.message }), 'error');
  } finally {
    btn.disabled = false; btn.textContent = t('area.add'); btn.classList.remove('ready');
  }
}

// ---------- typeahead + scope control ----------
let selectedArea = null;        // resolved suggestion (with centroid)
let suggestTimer = null;
let suggestions = [];
let activeSuggestion = -1;

function hideSuggestions() {
  const box = $('area-suggestions');
  box.hidden = true; box.innerHTML = '';
  suggestions = []; activeSuggestion = -1;
}

function renderSuggestions(results) {
  const box = $('area-suggestions');
  suggestions = results;
  if (!results.length) { hideSuggestions(); return; }
  box.innerHTML = '';
  results.forEach((r, i) => {
    const area = normalizeArea(r);
    const el = document.createElement('div');
    el.className = 'suggestion';
    el.dataset.idx = String(i);
    el.innerHTML = `<span class="s-label">${area.label}</span><span class="s-badge">${area.type || 'area'}</span>`;
    el.addEventListener('mousedown', (e) => { e.preventDefault(); pickSuggestion(i); });
    box.appendChild(el);
  });
  activeSuggestion = -1;
  box.hidden = false;
}

function pickSuggestion(i) {
  const r = suggestions[i];
  if (!r) return;
  selectedArea = normalizeArea(r);
  $('area-input').value = selectedArea.label;
  hideSuggestions();
  // Don't auto-add (felt abrupt). Instead make the next step obvious: the Add button lights up
  // and names what it'll add. One click (or Enter) adds it.
  const btn = $('area-add-btn');
  const short = selectedArea.label.length > 16 ? selectedArea.label.slice(0, 16) + '…' : selectedArea.label;
  btn.textContent = t('area.addShort', { name: short });
  btn.classList.add('ready');
  $('area-input').focus();
}

async function onAreaInput() {
  const q = $('area-input').value.trim();
  selectedArea = null; // typing invalidates a previous pick
  const addBtn = $('area-add-btn'); if (addBtn) { addBtn.textContent = t('area.add'); addBtn.classList.remove('ready'); }
  clearTimeout(suggestTimer);
  if (q.length < 2) { hideSuggestions(); return; }
  suggestTimer = setTimeout(async () => {
    try {
      const search = await api.geoSearch(q, 4, 8);
      renderSuggestions((search.results || []).slice(0, 8));
    } catch { hideSuggestions(); }
  }, 250);
}

function onAreaKeydown(e) {
  const box = $('area-suggestions');
  if (!box.hidden && suggestions.length) {
    if (e.key === 'ArrowDown') { e.preventDefault(); activeSuggestion = Math.min(suggestions.length - 1, activeSuggestion + 1); highlightSuggestion(); return; }
    if (e.key === 'ArrowUp') { e.preventDefault(); activeSuggestion = Math.max(0, activeSuggestion - 1); highlightSuggestion(); return; }
    if (e.key === 'Enter' && activeSuggestion >= 0) { e.preventDefault(); pickSuggestion(activeSuggestion); return; }
    if (e.key === 'Escape') { hideSuggestions(); return; }
  }
  if (e.key === 'Enter') { e.preventDefault(); addAreaFromInput(); }
}

function highlightSuggestion() {
  const box = $('area-suggestions');
  [...box.children].forEach((el, i) => el.classList.toggle('active', i === activeSuggestion));
}

function wireScopeControl() {
  const sel = $('scope-select');
  const wrap = $('scope-value-wrap');
  const val = $('scope-value');
  const unit = $('scope-unit');
  const apply = () => {
    const mode = sel.value;
    if (mode === 'area') { wrap.hidden = true; return; }
    wrap.hidden = false;
    if (mode === 'radius') { unit.textContent = 'km'; val.max = 50; val.value = val.value && Number(val.value) <= 50 ? val.value : 5; }
    else { unit.textContent = 'min'; val.max = 60; val.value = val.value && Number(val.value) <= 60 ? val.value : 20; }
  };
  sel.addEventListener('change', () => {
    // Reset to sensible default when switching mode.
    if (sel.value === 'radius') $('scope-value').value = 5;
    if (sel.value === 'drivetime') $('scope-value').value = 20;
    apply();
  });
  apply();
}

// One geo/search per postcode gives both the centroid and the display name; fill both caches.
async function pcLookup(pc) {
  const key = String(pc);
  if (pcCentroidCache.has(key) && pcNameCache.has(key)) {
    return { center: pcCentroidCache.get(key), name: pcNameCache.get(key) };
  }
  let center = null, name = key;
  try {
    const r = await api.geoSearch(key);
    const f = (r.results || [])[0];
    const lat = f?.center?.lat ?? f?.latitude, lng = f?.center?.lng ?? f?.longitude;
    if (lat != null && lng != null) center = { lat, lng };
    if (f) name = f.label || f.name || key;
  } catch { /* offline: no centroid, name = pc */ }
  pcCentroidCache.set(key, center);
  pcNameCache.set(key, name);
  return { center, name };
}

// Load an existing selection / zone from a spreadsheet (Distritool export, our own Excel export,
// or any sheet with a postcode column). Parsed in the browser — nothing is uploaded.
// Counts: the postcodes go straight into state.pcUnits (whole postcode, immediate recount).
// Map: per postcode the sectors around its centroid, tagged with that postcode — the same
// approximation the search box uses for a single pc4 — in a bounded pool.
async function handleExcelUpload(file) {
  const link = $('upload-excel-link');
  const t0 = Date.now();
  const logBase = { file: file.name, size: file.size };
  try {
    setStatus(t('st.reading', { name: file.name }));
    mapLoad('import', t('st.reading', { name: file.name }));
    const [{ parseSelectionSheet }, XLSX] = await Promise.all([import('./importSelection.js'), import('xlsx')]);
    const sel = parseSelectionSheet(XLSX, await file.arrayBuffer(), file.name);
    api.logEvent({ event: 'import', ...logBase, rows: sel.rowCount, headers: sel.headers, kinds: sel.kinds,
      sectors: sel.sectors.length, postcodes: sel.postcodes.length, municipalities: sel.municipalities.length, headerFound: sel.headerFound });
    if (!sel.postcodes.length && !sel.municipalities.length && !sel.sectors.length) {
      const cols = sel.headers.length ? t('st.colsFound', { cols: sel.headers.join(', ') }) : '';
      const why = sel.headerFound ? t('st.noRows', { name: file.name }) : t('st.noHeader', { name: file.name });
      setStatus(t('st.nothingImported', { cols }));
      toast(why + cols, 'error');
      api.logEvent({ event: 'import-empty', ...logBase, ms: Date.now() - t0, headers: sel.headers, kinds: sel.kinds });
      return;
    }
    // Statistical sectors (Distritool "Wijk" rows): select exactly those sectors — a segmented
    // zone is a handful of sectors per postcode, so taking the whole postcodes would inflate the
    // selection several times over. Sectors the map layer doesn't know fall back to their postcode.
    let sectorsAdded = 0, sectorsMissing = 0;
    if (sel.sectors.length) {
      setStatus(t('st.loadingSectors', { total: sel.sectors.length }));
      mapLoad('import', t('st.loadingSectors', { total: sel.sectors.length }));
      const found = await fetchSectorsByCodes(sel.sectors.map(s => s.nis9), (done, total) => {
        mapLoad('import', t('st.loadingSectorsProgress', { done, total }));
      });
      const byPc = new Map();
      for (const s of sel.sectors) {
        const info = found.get(s.nis9);
        if (!info) {
          sectorsMissing++;
          if (s.postcode && !sel.postcodes.includes(s.postcode)) sel.postcodes.push(s.postcode);
          continue;
        }
        const pc = s.postcode || await resolvePostcode(info.lat, info.lng) || '';
        if (!byPc.has(pc)) byPc.set(pc, []);
        byPc.get(pc).push(info);
      }
      // Municipality names are left blank on purpose: the table resolves them per NIS5 (one
      // cached lookup each) — a geo/search per postcode here cost ~3 s a piece.
      for (const [pc, infos] of byPc) sectorsAdded += addSectors(infos, pc || null, '');
      if (sectorsAdded) {
        // A sector list is a zone that was already segmented elsewhere (Distritool): start with
        // the threshold at 0 so nothing of it is filtered away until the user asks for it.
        setMinPct(0);
        refreshUI();
        scheduleRecount();
      }
    }

    const fresh = sel.postcodes.filter(pc => !state.pcUnits.has(pc));
    fresh.forEach(pc => state.pcUnits.add(pc));
    if (fresh.length) scheduleRecount(); // counts appear right away from the postcode set
    if (!sel.headerFound) toast(t('st.noHeaderTook', { name: file.name }), 'info');

    // Whole municipalities (Distritool NIS5 rows): the export carries one postcode per
    // municipality, so look the municipality up and select the entire area, exactly like typing
    // its name in the search box. Falls back to the single postcode when GIS doesn't know it.
    let muniDone = 0;
    for (const m of sel.municipalities) {
      setStatus(t('st.loadingMuni', { name: m.name, i: muniDone + 1, n: sel.municipalities.length }));
      mapLoad('import', t('st.loadingName', { name: m.name }));
      let handled = false;
      try {
        const search = await api.geoSearch(m.name);
        const results = search.results || [];
        const hit = results.find(r => r.type === 'municipality') || results[0];
        const area = normalizeArea(hit);
        if (area && area.center.lat != null) handled = await addAdminArea(area, { nis5: m.nis5 });
      } catch (e) { console.warn('municipality import failed', m, e); }
      if (handled) muniDone++;
      else if (m.postcode && !sel.postcodes.includes(m.postcode)) { sel.postcodes.push(m.postcode); if (!state.pcUnits.has(m.postcode)) { state.pcUnits.add(m.postcode); fresh.push(m.postcode); } }
    }

    // Map: sectors per postcode, bounded concurrency so a cold GIS isn't hammered.
    const centers = [];
    let shown = 0, missing = 0;
    await runPool(sel.postcodes, async (pc) => {
      const { center, name } = await pcLookup(pc);
      if (!center) { missing++; return; }
      centers.push(center);
      let sectors = [];
      try { sectors = await sectorsAround(center.lng, center.lat, 1.2); } catch { /* geoserver offline */ }
      let n = addSectors(sectors, pc, name);
      if (n === 0 && !state.sectors.has(`PC${pc}`) && !sectors.length) {
        // Offline / no sectors: keep the postcode visible in the table as one unit.
        state.sectors.set(`PC${pc}`, { nis9: `PC${pc}`, postcode: pc, municipality: name, totalHH: 0, feature: null });
        emit('sectors-changed');
        n = 1;
      }
      shown += n;
    }, 4, (done, total) => { setStatus(t('st.loadingZonesFrom', { done, total, label: sel.label })); mapLoad('import', t('st.loadingZones', { done, total })); });

    if (centers.length && !muniDone && !sectorsAdded) {
      const lngs = centers.map(c => c.lng), lats = centers.map(c => c.lat);
      const pad = 0.02;
      fitToBBox([Math.min(...lngs) - pad, Math.min(...lats) - pad, Math.max(...lngs) + pad, Math.max(...lats) + pad], 60);
    } else if (muniDone || sectorsAdded) {
      fitToSelection(60);
    }
    refreshUI();
    scheduleRecount();
    const dup = sel.postcodes.length - fresh.length;
    const parts = [`${sel.label}:`];
    if (muniDone) parts.push(t('st.imp.munis', { n: muniDone }));
    if (sectorsAdded) parts.push(t('st.imp.sectors', { n: sectorsAdded }));
    if (sectorsMissing) parts.push(t('st.imp.sectorsMissing', { n: sectorsMissing }));
    if (sel.postcodes.length) parts.push(t('st.imp.pcs', { n: sel.postcodes.length }));
    if (dup) parts.push(t('st.imp.dup', { n: dup }));
    if (missing) parts.push(t('st.imp.missing', { n: missing }));
    if (shown) parts.push(t('st.imp.zones', { n: shown }));
    setStatus(parts.join(' · ') + '.');
    api.logEvent({ event: 'import-done', ...logBase, ms: Date.now() - t0, sectorsAdded, sectorsMissing, municipalities: muniDone,
      postcodes: sel.postcodes.length, zones: shown, pcMissing: missing, selectionSectors: state.sectors.size, pcUnits: state.pcUnits.size });
    const what = [muniDone ? t('st.imp.munisShort', { n: muniDone }) : '', sectorsAdded ? t('st.imp.sectorsShort', { n: sectorsAdded }) : '', sel.postcodes.length ? t('st.imp.pcsShort', { n: sel.postcodes.length }) : ''].filter(Boolean).join(' + ');
    toast(t('st.imp.loaded', { label: sel.label, what }), 'success');
  } catch (e) {
    console.error(e);
    api.logEvent({ event: 'import-error', ...logBase, ms: Date.now() - t0, error: String(e && e.message || e).slice(0, 300) });
    setStatus(t('st.importError', { msg: e.message }));
    toast(t('st.couldNotImport', { msg: e.message }), 'error');
  } finally {
    mapLoad('import', null);
    if (link) link.textContent = t('upload.link');
  }
}

function scheduleRecount() {
  clearTimeout(recountTimer);
  state.loadingCounts = true; // a recount is pending → after-segmentation counts not yet ready
  refreshUI();                // reflect the loading state ("…") immediately, not a stale/0 value
  recountTimer = setTimeout(recount, 250);
}

let countProgress = null; // { done, total } while a recount is fetching, else null

async function recount() {
  const { mode, units } = countUnits();
  if (!units.length) { state.counts.clear(); state.loadingCounts = false; countProgress = null; refreshUI(); return; }
  state.loadingCounts = true;
  countProgress = { done: 0, total: units.length };
  const kCounting = mode === 'sector' ? 'st.countingSectors' : 'st.counting';
  const kProgress = mode === 'sector' ? 'st.countingSectorsProgress' : 'st.countingProgress';
  setStatus(t(kCounting, { n: units.length }));
  mapLoad('count', t(kCounting, { n: units.length }));
  refreshUI();
  try {
    const subsegs = [...state.dividuals];
    const sectorCodes = [...state.sectors.keys()].filter(k => !String(k).startsWith('PC'));
    const perSectorP = subsegs.length && sectorCodes.length
      ? gisSectors(sectorCodes, subsegs).catch((e) => { console.warn('GIS sector details failed', e.message); return new Map(); })
      : Promise.resolve(new Map());
    const perUnit = await fetchCounts(units, subsegs, (done, total) => {
      countProgress = { done, total };
      mapLoad('count', t(kProgress, { done, total }));
      if (done === total || done % 5 === 0) { setStatus(t(kProgress, { done, total })); refreshUI(); }
    }, { lang: state.waveLang });
    // Switch keys and values together so no consumer ever reads sector keys in postcode mode.
    const perSector = await perSectorP;
    state.countUnit = mode;
    state.counts = perUnit;
    state.sectorPct = new Map([...perSector].filter(([, r]) => r.total > 0).map(([k, r]) => [k, r.pct]));
    emit('counts-changed');
    if (lastCountSource() === 'gis-fallback') {
      setStatus(t('st.waveDown'));
      if (!fallbackToastShown) { fallbackToastShown = true; toast(t('st.waveDownToast'), 'info'); }
    } else {
      setStatus('');
    }
  } catch (e) {
    console.error(e);
    setStatus(t('st.countError', { msg: e.message }));
    toast(t('st.countError', { msg: e.message }), 'error');
  } finally {
    state.loadingCounts = false;
    countProgress = null;
    mapLoad('count', null);
    refreshUI();
  }
}

function setStatus(msg) { $('counts-status').textContent = msg || ''; }

let toastTimer = null;
function toast(msg, type = 'info') {
  const el = $('toast'); if (!el) return;
  el.textContent = msg; el.className = 'toast ' + type; el.hidden = false;
  clearTimeout(toastTimer); toastTimer = setTimeout(() => { el.hidden = true; }, 3500);
}

// Map loading chip. Multiple sources (counting, resolving zones) share one chip; it shows the
// most recent active message and hides only when every source has cleared.
const mapLoads = new Map();
function mapLoad(source, text) {
  if (text) mapLoads.set(source, text); else mapLoads.delete(source);
  const el = $('map-loading');
  if (!el) return;
  const msgs = [...mapLoads.values()];
  if (msgs.length) { $('map-loading-text').textContent = msgs[msgs.length - 1]; el.hidden = false; }
  else el.hidden = true;
}

// ---------- dividuals catalogue + corresponding list ----------
// Name in the interface language (the catalogue carries name_nl / name_fr / name_en), with
// Dutch as the fallback. Resolved at render time, never cached, so a language switch re-labels.
const nameOf = (o) => o[`name_${getLang()}`] || o.name_nl || o.name_en || o.name_fr ||
  (o.label && (o.label[getLang()] || o.label.nl || o.label.en || o.label.fr)) || o.code;

async function loadDividuals() {
  const listEl = $('dividuals-list');
  try {
    const data = await api.dividualsAttributes();
    catalogueSegments = data.segments || data.data || [];
    catalogueByCode.clear();
    catalogueSegments.forEach(seg => {
      (seg.subsegments || []).forEach(sub => {
        catalogueByCode.set(sub.code, {
          code: sub.code, color: sub.color || '#d9d9d9', segCode: seg.code, segColor: seg.color || '#999',
          get name() { return nameOf(sub); },     // live: follows the interface language
          get segName() { return nameOf(seg); }
        });
      });
    });
    if (!catalogueByCode.size) { listEl.innerHTML = `<div class="loading-note">${t('div.none')}</div>`; return; }
    // Initial render reflects any picker state (none by default -> full catalogue).
    updateCorresponding();
  } catch (e) {
    listEl.innerHTML = `<div class="loading-note">${t('div.failed', { msg: e.message })}</div>`;
  }
}

// Build one checkbox row for a subsegment code.
function dividualRow(code, { checked, matched, score }) {
  const meta = catalogueByCode.get(code);
  if (!meta) return null;
  const row = document.createElement('label');
  row.className = 'dividual-item' + (matched ? ' matched' : '');
  row.innerHTML =
    `<input type="checkbox" value="${meta.code}"${checked ? ' checked' : ''} />` +
    `<span class="swatch-pair">` +
      `<span class="swatch" style="background:${meta.segColor}"></span>` +
      `<span class="swatch" style="background:${meta.color}"></span>` +
    `</span>` +
    `<span class="dividual-text"><span class="seg">${meta.segCode} - ${meta.segName}</span><br>` +
    `<span class="sub">${meta.code} - ${meta.name}</span></span>` +
    (matched && score ? `<span class="match-score">${t(score > 1 ? 'div.matches' : 'div.match', { n: score })}</span>` : '');
  row.querySelector('input').addEventListener('change', (e) => {
    if (e.target.checked) state.dividuals.add(meta.code);
    else state.dividuals.delete(meta.code);
    scheduleRecount();
  });
  return row;
}

// Render the Dividuals grouped per segment (A "Dag tot dag" … J), each group with its own
// checkbox (all of its Dividuals at once) and a fold. Groups start folded; "expand all" opens
// them, and so does a picker match (matched = array of {code,score}; null = full catalogue).
const expandedGroups = new Set();
function renderDividualsList(matched) {
  const listEl = $('dividuals-list');
  const noteEl = $('dividuals-note');
  listEl.innerHTML = '';
  noteEl.hidden = !matched;
  if (matched && !matched.length) {
    listEl.innerHTML = `<div class="loading-note">${t('div.noMatch')}</div>`;
    return;
  }
  const expandAll = !!$('div-expand-all')?.checked;
  // code -> score for the matched view; group order = catalogue order.
  const scoreOf = new Map((matched || []).map(m => [m.code, m.score]));
  catalogueSegments.forEach(seg => {
    const subs = (seg.subsegments || []).filter(sub => !matched || scoreOf.has(sub.code));
    if (!subs.length) return;
    const codes = subs.map(sub => sub.code);
    const nChecked = codes.filter(c => state.dividuals.has(c)).length;
    const open = expandAll || !!matched || expandedGroups.has(seg.code);
    const grp = document.createElement('div');
    grp.className = 'dividual-group' + (matched ? ' matched' : '') + (open ? '' : ' collapsed');
    grp.innerHTML =
      `<div class="grp-head">` +
        `<input type="checkbox" class="grp-check" />` +
        `<span class="swatch" style="background:${seg.color || '#999'}"></span>` +
        `<span class="grp-name">${seg.code} - ${nameOf(seg)} <span class="grp-count">(${subs.length === 1 ? t('div.group1') : t('div.groupN', { n: subs.length })})</span></span>` +
        `<button type="button" class="grp-toggle">${open ? '▾' : '▸'}</button>` +
      `</div><div class="grp-body"></div>`;
    const check = grp.querySelector('.grp-check');
    check.checked = nChecked === codes.length;
    check.indeterminate = nChecked > 0 && nChecked < codes.length;
    check.addEventListener('change', () => {
      codes.forEach(c => { if (check.checked) state.dividuals.add(c); else state.dividuals.delete(c); });
      grp.querySelectorAll('.grp-body input[type=checkbox]').forEach(i => { i.checked = check.checked; });
      check.indeterminate = false;
      scheduleRecount();
    });
    const toggle = () => {
      const nowOpen = grp.classList.toggle('collapsed') === false;
      grp.querySelector('.grp-toggle').textContent = nowOpen ? '▾' : '▸';
      if (nowOpen) expandedGroups.add(seg.code); else expandedGroups.delete(seg.code);
    };
    grp.querySelector('.grp-toggle').addEventListener('click', toggle);
    grp.querySelector('.grp-name').addEventListener('click', toggle);
    const body = grp.querySelector('.grp-body');
    subs.forEach(sub => {
      const row = dividualRow(sub.code, { checked: state.dividuals.has(sub.code), matched: !!matched, score: scoreOf.get(sub.code) });
      if (!row) return;
      // Keep the group box in step with its rows.
      row.querySelector('input').addEventListener('change', () => {
        const n = codes.filter(c => state.dividuals.has(c)).length;
        check.checked = n === codes.length;
        check.indeterminate = n > 0 && n < codes.length;
      });
      body.appendChild(row);
    });
    listEl.appendChild(grp);
  });
}

// Recompute the corresponding Dividuals from the current picker state and re-render.
function updateCorresponding() {
  const res = correspondingSubsegments({
    familySizes: state.friendly.familySizes,
    houseTypes: state.friendly.houseTypes,
    ageRange: state.friendly.ageRange,
    incomeRange: state.friendly.incomeRange
  });
  if (res.any) {
    // Pre-check all matched -> these drive the live count.
    state.dividuals = new Set(res.matched.map(m => m.code).filter(c => catalogueByCode.has(c)));
    lastMatched = res.matched.filter(m => catalogueByCode.has(m.code));
    renderDividualsList(lastMatched);
  } else {
    lastMatched = null;
    // Pickers just went from set -> empty: clear the auto-derived selection so
    // "no pickers" == "no filter". (Manual full-catalogue checks aren't touched here,
    // since this branch only runs on picker changes.)
    if (lastPickerAny) state.dividuals.clear();
    renderDividualsList(null);
  }
  lastPickerAny = res.any;
  scheduleRecount();
}

// ---------- friendly pickers ----------
function wirePickers() {
  const wireGrid = (gridId, setKey) => {
    const grid = $(gridId);
    grid.querySelectorAll('.picker-tile').forEach(tile => {
      tile.addEventListener('click', () => {
        const set = state.friendly[setKey];
        const v = tile.dataset.value;
        if (set.has(v)) { set.delete(v); tile.classList.remove('selected'); }
        else { set.add(v); tile.classList.add('selected'); }
        updateCorresponding();
      });
    });
  };
  $('div-expand-all')?.addEventListener('change', () => { if (catalogueByCode.size) renderDividualsList(lastMatched); });
  wireGrid('family-size-grid', 'familySizes');
  wireGrid('house-type-grid', 'houseTypes');

  ageRangeCtl = initDualRange($('age-range'), {
    onChange: (range) => { state.friendly.ageRange = range; updateCorresponding(); }
  });
  // Income = an index range like Distritool's slider (100 = modal income, 200 = twice modal).
  // The full range imposes no constraint.
  incomeRangeCtl = initDualRange($('income-range'), {
    onChange: (range) => { state.friendly.incomeRange = range; updateCorresponding(); }
  });
}

// ---------- selection panel controls ----------
// Set the threshold programmatically (slider, label, state, re-filter) — same path as a drag.
function setMinPct(pct) {
  const slider = $('min-pct-slider');
  const v = Math.min(Number(slider.max), Math.max(Number(slider.min), Math.round(pct)));
  slider.value = String(v);
  slider.dispatchEvent(new Event('input', { bubbles: true }));
}
function wireSelectionPanel() {
  const slider = $('min-pct-slider');
  slider.addEventListener('input', () => {
    state.minTargetPct = Number(slider.value);
    $('min-pct-value').textContent = slider.value;
    // Client-side re-filter only — no refetch (build brief §5.3).
    refreshUI();
  });
  // − / + step buttons: change the value by 1% (clamped) and reuse the slider handler.
  const stepPct = (delta) => {
    const min = Number(slider.min), max = Number(slider.max);
    const next = Math.min(max, Math.max(min, Number(slider.value) + delta));
    if (next === Number(slider.value)) return;
    slider.value = String(next);
    slider.dispatchEvent(new Event('input', { bubbles: true }));
  };
  $('min-pct-dec').addEventListener('click', () => stepPct(-1));
  $('min-pct-inc').addEventListener('click', () => stepPct(1));

  $('include-email').addEventListener('change', (e) => {
    state.includeSegmentedEmail = e.target.checked;
  });

  // Everything the order document needs: the panel figures, the segmentation definition, the
  // surviving postcodes and the saved link (so The Wave can reproduce the exact selection).
  const num = (id) => Number($(id).textContent.replace(/\D/g, '')) || 0;
  const summary = () => {
    const split = postcodeSplit();
    const savedId = new URLSearchParams(location.search).get('s');
    return {
      name: $('save-name').value.trim() || '',
      link: savedId ? `${location.origin}${location.pathname}?s=${savedId}` : '',
      bpIn: num('bp-in'), bpAfter: num('bp-after'),
      dmIn: num('dm-in'), dmAfter: num('dm-after'),
      emIn: num('em-in'), emAfter: num('em-after'),
      phoneIn: num('phone-in'), phoneAfter: num('phone-after'),
      minPct: state.minTargetPct,
      includeEmail: !!state.includeSegmentedEmail,
      waveLang: state.waveLang || null,
      dividuals: [...state.dividuals].sort().map((code) => {
        const m = catalogueByCode.get(code);
        return { code, name: m ? m.name : '', segment: m ? `${m.segCode} - ${m.segName}` : '' };
      }),
      friendly: {
        familySizes: [...state.friendly.familySizes],
        houseTypes: [...state.friendly.houseTypes],
        ageRange: [...state.friendly.ageRange],
        incomeRange: [...state.friendly.incomeRange]
      },
      postcodes: split.kept,
      postcodesDropped: split.dropped,
      countUnit: state.countUnit,
      countSource: lastCountSource() === 'instadata' ? t('countSource.live') : t('countSource.gis')
    };
  };

  const visible = () => {
    const dropped = droppedSectors();
    return new Set([...state.sectors.keys()].filter(n => !dropped.has(n)));
  };

  $('place-order-btn').addEventListener('click', async () => {
    if (!state.sectors.size) { setStatus(t('st.selectFirst')); return; }
    setStatus(t('st.preparingExport'));
    const { exportPdf, exportExcel } = await loadExport();
    const sm = summary();
    const base = (sm.name || 'segmentatie-order').replace(/[^\w.-]+/g, '_');
    exportPdf(sm, visible(), 'order', `${base}.pdf`);
    exportExcel(sm, visible(), `${base}.xlsx`);
    setStatus(t('st.orderExported'));
  });
  $('request-quote-btn').addEventListener('click', async () => {
    if (!state.sectors.size) { setStatus(t('st.selectFirst')); return; }
    setStatus(t('st.preparingExport'));
    const { exportPdf } = await loadExport();
    const sm = summary();
    exportPdf(sm, visible(), 'quote', `${(sm.name || 'segmentatie-offerte').replace(/[^\w.-]+/g, '_')}-offerte.pdf`);
    setStatus(t('st.quoteExported'));
  });
}

// ---------- draw toolbar ----------
function setActiveDrawBtn(id) {
  const eff = id || 'draw-select-btn'; // null / draw-finished / clear -> back to Select (default)
  ['draw-select-btn', 'draw-circle-btn', 'draw-polygon-btn'].forEach(b => $(b)?.classList.toggle('active', b === eff));
}
function wireDrawToolbar() {
  $('draw-select-btn').addEventListener('click', () => { stopDraw(); setSelectEnabled(true); setActiveDrawBtn('draw-select-btn'); setStatus(t('st.clickZones')); });
  $('draw-circle-btn').addEventListener('click', () => { setActiveDrawBtn('draw-circle-btn'); startDrawCircle(); setStatus(t('st.drawCircle')); });
  $('draw-polygon-btn').addEventListener('click', () => { setActiveDrawBtn('draw-polygon-btn'); startDrawPolygon(); setStatus(t('st.drawPolygon')); });
  let clearArmed = false, clearTimer = null;
  $('draw-clear-btn').addEventListener('click', () => {
    const btn = $('draw-clear-btn');
    if (!clearArmed) { // first click: arm + ask for confirmation
      clearArmed = true;
      btn.textContent = t('tool.clear.again');
      btn.classList.add('armed');
      clearTimeout(clearTimer);
      clearTimer = setTimeout(() => { clearArmed = false; btn.textContent = t('tool.clear'); btn.classList.remove('armed'); }, 3000);
      setStatus(t('st.clearAgain'));
      return;
    }
    clearTimeout(clearTimer); clearArmed = false;               // second click: actually clear
    btn.textContent = t('tool.clear'); btn.classList.remove('armed');
    stopDraw(); setSelectEnabled(true); setActiveDrawBtn(null); clearSelection(); refreshUI();
    setStatus(t('st.cleared'));
  });
}

// ---------- init ----------

// ---------- save & share link ----------
// The whole selection (area, segmentation, threshold) is stored under a short id in KV, through
// the Function; the link ?s=<id> restores it. The sector GEOMETRY is not stored: on restore the
// sectors are re-fetched from the WFS by the saved bbox and matched on NIS9.
function pickerCtx() {
  return {
    $, state, emit, refreshUI, scheduleRecount, updateCorresponding, renderDividualsList,
    getLastMatched: () => lastMatched, setLastPickerAny: (v) => { lastPickerAny = v; },
    ageRangeCtl, incomeRangeCtl, catalogueByCode, dividualsReady, fetchSectorsInBBox, fetchSectorsByCodes, fitToSelection,
    setStatus, toast, mapLoad, nis5NameCache
  };
}

function selectionSummary() {
  return {
    sectors: state.sectors.size,
    postcodes: selectedPostcodes().length,
    dividuals: state.dividuals.size,
    minTargetPct: state.minTargetPct,
    bpIn: lastBp.all || 0
  };
}

async function saveCurrent() {
  if (!state.sectors.size && !state.pcUnits.size) { setSaveStatus(t('st.selectFirst')); return; }
  const btn = $('save-btn');
  btn.disabled = true;
  try {
    setSaveStatus(t('save.saving'));
    const name = $('save-name').value.trim() || t('save.defaultName', { date: new Date().toLocaleDateString(locale()) });
    const res = await api.saveSelection({ name, summary: selectionSummary(), selection: serializeSelection(state) });
    const url = `${location.origin}${location.pathname}?s=${res.id}`;
    $('save-link').value = url;
    $('save-result').hidden = false;
    history.replaceState(null, '', `?s=${res.id}`);
    setSaveStatus(t('save.saved', { name }));
    try { await navigator.clipboard.writeText(url); toast(t('save.copied'), 'success'); } catch { /* clipboard blocked */ }
    loadSavedList();
  } catch (e) {
    setSaveStatus(t('save.couldNotSave', { msg: e.message }));
  } finally {
    btn.disabled = false;
  }
}

async function openSaved(id) {
  try {
    setSaveStatus(t('save.loading'));
    mapLoad('restore', t('save.loading'));
    const doc = await api.getSelection(id);
    await restoreSelection(doc.selection, pickerCtx());
    $('save-name').value = doc.name || '';
    $('save-link').value = `${location.origin}${location.pathname}?s=${doc.id}`;
    $('save-result').hidden = false;
    history.replaceState(null, '', `?s=${doc.id}`);
    setSaveStatus(t('save.opened', { name: doc.name, date: String(doc.createdAt).slice(0, 10) }));
    toast(t('save.restored', { name: doc.name }), 'success');
  } catch (e) {
    console.error(e);
    setSaveStatus(t('save.couldNotOpen', { msg: e.message }));
    toast(t('save.couldNotOpenToast', { msg: e.message }), 'error');
  } finally {
    mapLoad('restore', null);
  }
}

async function loadSavedList() {
  const sel = $('saved-list');
  try {
    const { items } = await api.listSelections();
    const current = sel.value;
    sel.innerHTML = `<option value="">${t('save.openPlaceholder')}</option>`;
    (items || []).forEach((it) => {
      const o = document.createElement('option');
      o.value = it.id;
      const d = String(it.createdAt || '').slice(0, 10);
      const sm = it.summary || {};
      o.textContent = t('save.listItem', { name: it.name, date: d, pcs: sm.postcodes ? t('save.listPcs', { n: sm.postcodes }) : '', hh: sm.bpIn ? t('save.listHh', { n: fmt(sm.bpIn) }) : '' });
      sel.appendChild(o);
    });
    if (current) sel.value = current;
  } catch { /* list is a convenience; stay silent */ }
}

function setSaveStatus(msg) { $('save-status').textContent = msg || ''; }

// Leave the saved selection: empty area, default segmentation, no ?s= in the address bar. The
// saved one is untouched — it stays in the list and under its link.
function startNewSelection() {
  stopDraw(); setSelectEnabled(true); setActiveDrawBtn(null);
  clearAreaCircle();
  clearSelection();
  // segmentation back to "no filter"
  ['familySizes', 'houseTypes'].forEach((k) => state.friendly[k].clear());
  state.friendly.ageRange = [18, 90];
  state.friendly.incomeRange = [INCOME_MIN, INCOME_MAX];
  document.querySelectorAll('#family-size-grid .picker-tile, #house-type-grid .picker-tile').forEach((t) => t.classList.remove('selected'));
  if (ageRangeCtl) ageRangeCtl.set(18, 90);
  if (incomeRangeCtl) incomeRangeCtl.set(INCOME_MIN, INCOME_MAX);
  state.dividuals.clear();
  lastPickerAny = false; lastMatched = null;
  if (catalogueByCode.size) renderDividualsList(null);
  // threshold + e-mail
  state.minTargetPct = 0;
  $('min-pct-slider').value = '0'; $('min-pct-value').textContent = '0';
  state.includeSegmentedEmail = false; $('include-email').checked = false;
  state.waveLang = null;
  document.querySelectorAll('.wave-lang button').forEach((b) => b.classList.toggle('selected', !b.dataset.waveLang));
  // saved-link context
  $('save-name').value = ''; $('save-link').value = '';
  $('save-result').hidden = true;
  $('saved-list').value = '';
  history.replaceState(null, '', location.pathname);
  refreshUI();
  setStatus('');
  setSaveStatus(t('save.newDone'));
}

function wireSaveBlock() {
  $('save-btn').addEventListener('click', saveCurrent);
  $('save-name').addEventListener('keydown', (e) => { if (e.key === 'Enter') saveCurrent(); });
  $('save-copy-btn').addEventListener('click', async () => {
    const url = $('save-link').value;
    try { await navigator.clipboard.writeText(url); toast(t('save.copied'), 'success'); }
    catch { $('save-link').select(); }
  });
  $('new-selection-btn').addEventListener('click', startNewSelection);
  $('saved-open-btn').addEventListener('click', () => { const id = $('saved-list').value; if (id) openSaved(id); });
  $('saved-list').addEventListener('change', () => { const id = $('saved-list').value; if (id) openSaved(id); });
  loadSavedList();
}

// Language switch: swap static texts, re-render everything that holds text, keep the selection.
function wireLanguage() {
  document.querySelectorAll('.wave-lang button').forEach((b) => {
    b.addEventListener('click', () => {
      const v = b.dataset.waveLang || null;
      if (v === state.waveLang) return;
      state.waveLang = v;
      document.querySelectorAll('.wave-lang button').forEach((x) => x.classList.toggle('selected', x === b));
      scheduleRecount();
    });
  });
  document.querySelectorAll('.lang-switch:not(.wave-lang) button').forEach((b) => {
    b.addEventListener('click', () => {
      if (b.dataset.lang === getLang()) return;
      setLang(b.dataset.lang);
      refreshUI();
      if (catalogueByCode.size) renderDividualsList(lastMatched);
      loadSavedList();
      setStatus('');
    });
  });
}

function init() {
  applyStatic();
  wireLanguage();
  initMap('map', { onToggle: onSectorToggle, onDrawSelect: (infos) => { setSelectEnabled(true); setActiveDrawBtn(null); onDrawSelect(infos); } });
  setSelectEnabled(true); // Select is the default: a click toggles a sector, a drag pans

  makeDraggable($('configure-panel'), $('configure-panel-header'));
  makeDraggable($('selection-panel'), $('selection-panel-header'));

  $('area-add-btn').addEventListener('click', addAreaFromInput);
  $('area-input').addEventListener('input', onAreaInput);
  $('area-input').addEventListener('keydown', onAreaKeydown);
  $('area-input').addEventListener('blur', () => setTimeout(hideSuggestions, 150));
  wireScopeControl();
  wireDrawToolbar();
  $('goto-step2-btn').addEventListener('click', () => {
    const seg = document.getElementById('collapseSeg');
    if (seg) Collapse.getOrCreateInstance(seg).show();
  });
  $('upload-excel-link').addEventListener('click', (e) => { e.preventDefault(); $('excel-file-input').click(); });
  $('excel-file-input').addEventListener('change', (e) => {
    const f = e.target.files[0];
    if (f) handleExcelUpload(f);
    e.target.value = ''; // allow re-importing the same file after a Clear
  });

  wirePickers();
  wireSelectionPanel();

  on('sectors-changed', refreshUI);
  on('sectors-changed', () => { ensureBpostBoxes(); });
  initBpostBoxes(() => { renderTable(); updateSelectionPanel(); });
  on('counts-changed', refreshUI);

  dividualsReady = loadDividuals();
  wireSaveBlock();
  refreshUI();

  // ?s=<id>: restore a saved selection (link from "Save & share link").
  const savedId = new URLSearchParams(location.search).get('s');
  if (savedId) openSaved(savedId);

  // Unhandled errors go to the event log too (a few per session), so a "keeps loading" report
  // can be traced without a screen share.
  let errorsLogged = 0;
  const logError = (kind, msg) => {
    if (errorsLogged++ >= 5) return;
    api.logEvent({ event: 'js-error', kind, error: String(msg || '').slice(0, 300) });
  };
  window.addEventListener('error', (e) => logError('error', e.message));
  window.addEventListener('unhandledrejection', (e) => logError('unhandledrejection', e.reason && (e.reason.message || e.reason)));

  // Warm the GIS geo path (JWT auth handshake + GIS backend) in the background right after load,
  // so the user's FIRST search isn't stuck behind a cold-isolate mint (many seconds on a cold GIS).
  api.geoSearch('brugge').catch(() => {});
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
